import {
    getConfiguredToolStepLabel,
    isDomainRenderedToolOutput,
    type ToolStepLabel,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_renderers';
import {
    ASK_USER_QUESTION_TOOL_NAME,
    EXECUTION_PLAN_TOOL_NAME,
    isUserInputToolName,
    QUERY_TOOL_NAME,
    REQUEST_USER_DECISION_TOOL_NAME,
    SHOW_DYNAMIC_UI_TOOL_NAME,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_name_constants';

export type ProjectedToolStep = {
    readonly groupKey: string;
    readonly labels: ToolStepLabel;
};

function formatDescriptionStepTitle(
    toolArguments: Record<string, unknown> | Array<unknown> | null,
): string | null {
    if (toolArguments === null || Array.isArray(toolArguments)) {
        return null;
    }
    const description = toolArguments['description'];
    if (typeof description !== 'string') {
        return null;
    }
    const normalizedDescription = description.trim().replace(/\s+/g, ' ');
    if (normalizedDescription.length === 0) {
        return null;
    }
    return `${normalizedDescription.charAt(0).toLocaleUpperCase()}${normalizedDescription.slice(1)}`;
}

export function isShimmerProgressTool(toolName: string | null): boolean {
    return (
        toolName !== null &&
        toolName !== EXECUTION_PLAN_TOOL_NAME &&
        !isUserInputToolName(toolName)
    );
}

// Map each tool name to a coordinator-facing step label. Tools sharing
// the same label collapse into ONE plan item (the agent firing 3 search
// tools during "Find shifts" produces 1 step with 3 nested tools, not
// 3 steps). null = boring infrastructure call, hide from the plan.
//
// The plan card is a PROJECTION of the run's actual tool stream — no
// agent narration, no update_execution_plan calls, no extra LLM turns
// for status bookkeeping. The runtime reads
// `phoebe_agent_run_events.tool_call` / `tool_output` events and
// renders the projection live.
export function lookupToolStepLabel(
    toolName: string | null,
): ToolStepLabel | null {
    if (toolName === null || toolName === EXECUTION_PLAN_TOOL_NAME) {
        return null;
    }
    const configuredLabel = getConfiguredToolStepLabel(toolName);
    if (configuredLabel !== undefined) {
        return configuredLabel;
    }
    return null;
}

export function getProjectedToolStep({
    toolName,
    toolArguments,
}: {
    readonly toolName: string | null;
    readonly toolArguments: Record<string, unknown> | Array<unknown> | null;
}): ProjectedToolStep | null {
    if (toolName === QUERY_TOOL_NAME || toolName === 'run_bash') {
        const title = formatDescriptionStepTitle(toolArguments);
        if (title === null) {
            return {
                groupKey: toolName,
                labels:
                    toolName === QUERY_TOOL_NAME
                        ? {
                              active: 'Querying records',
                              complete: 'Queried records',
                          }
                        : (getConfiguredToolStepLabel(toolName) ?? {
                              active: 'Running tool',
                              complete: 'Completed tool',
                          }),
            };
        }
        return {
            groupKey: `${toolName}:${title.toLocaleLowerCase()}`,
            labels: { active: title, complete: title },
        };
    }

    const labels = lookupToolStepLabel(toolName);
    return labels === null || toolName === null
        ? null
        : { groupKey: toolName, labels };
}

const DECLARED_PLAN_HIDDEN_TOOL_NAMES = new Set<string>([
    ASK_USER_QUESTION_TOOL_NAME,
    REQUEST_USER_DECISION_TOOL_NAME,
    SHOW_DYNAMIC_UI_TOOL_NAME,
    'date_and_time',
    'get_current_datetime',
    'load_skill',
    'organization_scheduling_context',
    'view_skills',
]);

export function shouldHideToolNodeForDeclaredPlan(
    toolName: string | null,
): boolean {
    if (toolName === null || DECLARED_PLAN_HIDDEN_TOOL_NAMES.has(toolName)) {
        return true;
    }
    return (
        lookupToolStepLabel(toolName) === null &&
        !isDomainRenderedToolOutput(toolName)
    );
}
